/**
 * Types shared between the server and the web SPA.
 * Server code imports these with `.js` extensions (NodeNext); the web app
 * imports them via the `@shared` vite alias or a relative path.
 */

// ---------------------------------------------------------------------------
// Spans — the canonical body representation for articles and tweets
// ---------------------------------------------------------------------------

export interface Span {
  /** the exact words to publish */
  text: string;
  sourceType: 'message' | 'media' | 'source_doc';
  /** messages.id or media_assets.id */
  sourceId: number;
  /** paragraph group, 0-based */
  para: number;
  /** render as attributed blockquote (source_doc only) */
  quote?: boolean;
}

// ---------------------------------------------------------------------------
// Pipeline / status enums (mirrors of the DB check-free text columns)
// ---------------------------------------------------------------------------

export type StageStatus = 'pending' | 'processing' | 'done' | 'skipped' | 'failed';
export type IntakeDecision = 'include' | 'ignore';
/** How the stored intake verdict was resolved. */
export type IntakeResolutionSource = 'model' | 'threshold_override' | 'request_fallback';
/** Stored artifact classes, including retired formats retained for history. */
export const OUTPUT_TYPES = ['article', 'tweet', 'quote_tweet', 'reply'] as const;
export type OutputType = (typeof OUTPUT_TYPES)[number];
export const ACTIVE_OUTPUT_TYPES = ['article', 'tweet'] as const;
export type ActiveOutputType = (typeof ACTIVE_OUTPUT_TYPES)[number];
export function isOutputType(kind: string): kind is OutputType {
  return OUTPUT_TYPES.includes(kind as OutputType);
}
export const RETIRED_OUTPUT_MESSAGE = 'Reply and quote-post publication has been retired. Historical records remain available.';
export function isActiveOutputType(kind: OutputType): kind is ActiveOutputType {
  return kind === 'article' || kind === 'tweet';
}
export type IdeaKind = OutputType;
/** The only user-facing lifecycle shared by every output type. */
export type IdeaStage = 'incubating' | 'needs_approval' | 'scheduled' | 'published';
export type ArticleFormat = OutputType;

/** Post-shaped records, including historical reply and quote records. */
export function isPostFormat(format: ArticleFormat): boolean {
  return format === 'tweet' || format === 'quote_tweet' || format === 'reply';
}
/** Internal delivery condition. This is deliberately not an output lifecycle. */
export type DeliveryStatus = 'pending' | 'publishing' | 'published' | 'failed';
export type RunStatus = 'running' | 'succeeded' | 'failed';
export type RunTrigger = 'cron' | 'manual' | 'chained';
export type MediaKind = 'voice' | 'audio' | 'video' | 'image' | 'pdf' | 'doc' | 'link';
/** Human-editable provenance classification for archived source material. */
export type LibraryOrigin = 'collective_original' | 'third_party' | 'agent_generated';
export type LibraryObjectType = 'media' | 'publication';
export type LibraryKind = MediaKind | ArticleFormat;
export type ChunkSourceType =
  | 'message'
  | 'media_transcript'
  | 'media_description'
  | 'source_doc'
  | 'link'
  | 'agent_generated'
  | 'publication';
/**
 * `chunks.sourceType` values whose `sourceId` is a `media_assets.id`. The two
 * id spaces (messages / media_assets) are independent bigserial sequences that
 * collide constantly, so EVERY query that filters `chunks` by a media asset id
 * must also constrain `source_type` — otherwise it silently matches an
 * unrelated message chunk with the same numeric id.
 */
export const MEDIA_CHUNK_SOURCE_TYPES: ChunkSourceType[] = [
  'media_transcript',
  'media_description',
  'source_doc',
  'link',
  'agent_generated',
];

/**
 * Canonical input material agents may retrieve for grounded composition.
 * Published outputs live in the same vector table for Library search, but are
 * deliberately absent here so they can never become self-referential source
 * material for the Curator, Editor, or Trends agents.
 */
export const SOURCE_MATERIAL_CHUNK_TYPES: ChunkSourceType[] = [
  'message',
  'media_transcript',
  'media_description',
  'source_doc',
  'link',
];

/** Everything the user-facing Library search is allowed to retrieve. */
export const LIBRARY_CHUNK_SOURCE_TYPES: ChunkSourceType[] = [
  ...SOURCE_MATERIAL_CHUNK_TYPES,
  'agent_generated',
  'publication',
];

export type Mode = 'auto' | 'review';

export function publicationReadyStage(mode: Mode): 'needs_approval' | 'scheduled' {
  return mode === 'review' ? 'needs_approval' : 'scheduled';
}
export type CostCategory =
  | 'curator'
  | 'trends'
  | 'analyst'
  | 'editor'
  | 'intake'
  | 'embeddings'
  | 'reranking'
  | 'vision'
  | 'image_generation'
  | 'transcription'
  | 'chronicler';

// ---------------------------------------------------------------------------
// Score components (ideas.score_components)
// ---------------------------------------------------------------------------

export interface ScoreComponents {
  originality: number;
  specificity: number;
  resonance: number;
  density: number;
}

export const SCORE_WEIGHTS: Record<keyof ScoreComponents, number> = {
  originality: 0.3,
  specificity: 0.2,
  resonance: 0.2,
  density: 0.3,
};

export function computeScore(c: Partial<ScoreComponents> | null | undefined): number {
  if (!c) return 0;
  let total = 0;
  for (const k of Object.keys(SCORE_WEIGHTS) as (keyof ScoreComponents)[]) {
    total += (Number(c[k]) || 0) * SCORE_WEIGHTS[k];
  }
  return Math.round(total * 100) / 100;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface ModelsConfig {
  curator: string;
  editor: string;
  embeddings: string;
  reranker: string;
  transcription: string;
  youtubeTranscription: string;
  vision: string;
  intakeClassifier: string;
  previewGeneration: string;
  [key: string]: string;
}

/** A selector-ready model option from the live OpenRouter catalog. */
export type ModelCatalogSource = 'openrouter' | 'fallback';

export interface ModelCatalogOption {
  id: string;
  label: string;
  inputModalities: string[];
  priceLabel: string;
  source: ModelCatalogSource;
}

export interface ModelCatalogResponse {
  fetchedAt: string;
  stale: boolean;
  options: ModelCatalogOption[];
}

export type ModelSelectorRole =
  | 'curator'
  | 'editor'
  | 'vision'
  | 'embeddings'
  | 'reranker'
  | 'transcription'
  | 'youtubeTranscription'
  | 'previewGeneration';

const CURATED_ROLE_MODEL_IDS = [
  'google/gemini-3.8-flash',
  'google/gemini-3.5-flash-lite',
  'google/gemini-3-flash-preview',
  'google/gemini-3.1-flash-lite',
  'google/gemini-3.6-flash',
  'google/gemini-2.5-flash-lite',
  'anthropic/claude-sonnet-5',
  'anthropic/claude-opus-5',
  'anthropic/claude-fable-5.1',
  'openai/gpt-6-astra',
  'openai/gpt-5.6-sol',
  'openai/gpt-5.6-terra',
  'openai/gpt-5.6-luna',
  'x-ai/grok-4.6',
] as const;

const CONVERSATIONAL_MODEL_IDS = [
  ...CURATED_ROLE_MODEL_IDS,
  'deepseek/deepseek-v4.1-flash',
  'deepseek/deepseek-v4-pro-0813',
  'deepseek/deepseek-v4-flash-0731',
  'deepseek/deepseek-v4-pro',
] as const;

/** Intentional, role-specific model selector choices (not a raw provider dump). */
export const MODEL_SELECTOR_IDS: Record<ModelSelectorRole, readonly string[]> = {
  curator: CONVERSATIONAL_MODEL_IDS,
  editor: CONVERSATIONAL_MODEL_IDS,
  // V4.1 Flash supports images; the older DeepSeek choices are text-only.
  vision: [...CURATED_ROLE_MODEL_IDS, 'deepseek/deepseek-v4.1-flash'],
  embeddings: ['openai/text-embedding-3-large', 'qwen/qwen3-embedding-8b'],
  reranker: ['cohere/rerank-4-fast'],
  transcription: [
    'microsoft/mai-transcribe-2',
    'openai/whisper-large-v3-turbo',
    'openai/whisper-large-v3',
    'qwen/qwen3-asr-flash-2026-02-10',
    'qwen/qwen3-asr-1.7b',
    'qwen/qwen3-asr-0.6b',
    'google/chirp-3',
  ],
  youtubeTranscription: [
    'google/gemini-3.8-flash',
    'google/gemini-3.5-flash-lite',
    'google/gemini-3.1-flash-lite',
    'google/gemini-2.5-flash-lite',
  ],
  previewGeneration: ['black-forest-labs/flux.2-klein-4b'],
};

export const DEFAULT_QUOTE_TWEET_STYLE_GUIDE =
  'Select one exact member passage that adds an original, substantive idea to the source post. ' +
  'The passage must make sense beside the quoted post without invented setup, paraphrase, hashtags, or trend-chasing language. ' +
  'Prefer disagreement, extension, or a surprising conceptual connection over simple endorsement.';

export const DEFAULT_REPLY_STYLE_GUIDE =
  'Select one exact member passage that makes a specific, positive contribution to the target post. ' +
  'It may support, endorse, extend, clarify, or add useful evidence, but it must be genuinely related to that exact post. ' +
  'Never paraphrase, splice, introduce, flatter generically, promote unrelated work, or add any words.';

/** Immutable snapshot of the X post a Tentacle reply targets. */
export interface ReplyTarget {
  postId: string;
  url: string;
  authorId: string | null;
  username: string | null;
  text: string;
  createdAt: string | null;
}

export interface SettingsShape {
  id: number;
  brandContext: string;
  styleGuide: string;
  /** Tweet-specific selection guidance; article prose continues to use styleGuide. */
  tweetStyleGuide: string;
  /** Guidance for selecting a member contribution to accompany a quoted source post. */
  quoteTweetStyleGuide: string;
  /** Guidance for selecting exact member words to reply to a specific post. */
  replyStyleGuide: string;
  articleCadenceCron: string;
  tweetSlots: number;
  mode: Mode;
  maxWords: number;
  strictTitle: boolean;
  announceInGroup: boolean;
  models: ModelsConfig;
  /** per-agent overrides, keyed by agent slug */
  agentSettings: Record<string, AgentSettingOverride>;
  updatedAt: string | null;
}

/**
 * Exact configured hours, or evenly spaced hour-of-day defaults for N tweet
 * slots (a 09:00–19:00 waking window). Shared because the scheduler derives
 * real cron jobs from it and the Settings page previews the same times: two
 * copies would let the dashboard advertise slots the scheduler never fires.
 */
export function tweetSlotHours(slots: number, configured?: number[]): number[] {
  if (configured !== undefined) {
    return [...new Set(configured.filter((h) => Number.isInteger(h) && h >= 0 && h <= 23))]
      .sort((a, b) => a - b);
  }
  const n = Math.max(0, Math.min(24, Math.floor(slots)));
  if (n === 0) return [];
  const first = 9;
  const last = 19;
  if (n === 1) return [Math.round((first + last) / 2)];
  const step = (last - first) / (n - 1);
  const hours = new Set<number>();
  for (let i = 0; i < n; i++) hours.add(Math.round(first + step * i));
  return [...hours].sort((a, b) => a - b);
}

/** X's standard post limit. Keep discovery and publication on one constant. */
export const TWEET_MAX_CHARS = 280;

/**
 * X counts a post's length in *weighted* characters, not JS code units: code
 * points inside the ranges below weigh 1, everything else (CJK, most emoji,
 * and anything outside the BMP) weighs 2. Counting `text.length` instead lets
 * a 280-code-unit selection containing any CJK sail through discovery and
 * composition and then get rejected by the API at publish time — where tweets
 * fail closed and never retry. Ranges are twitter-text's v3 configuration.
 */
const TWEET_WEIGHT_ONE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0000, 0x10ff],
  [0x2000, 0x200d],
  [0x2010, 0x201f],
  [0x2032, 0x2037],
];

export function tweetWeightedLength(text: string): number {
  let weight = 0;
  for (const char of text) {
    const cp = char.codePointAt(0)!;
    weight += TWEET_WEIGHT_ONE_RANGES.some(([lo, hi]) => cp >= lo && cp <= hi) ? 1 : 2;
  }
  return weight;
}

/**
 * The single expression of "does this fit in a post?" — returns null when it
 * fits, or the human-readable reason when it does not. Every gate uses it
 * (readiness, discovery, composition, the review routes, the publisher) so one
 * text can never be schedulable by one authority and impossible by another.
 */
export function tweetLengthError(text: string): string | null {
  const weighted = tweetWeightedLength(text.trim());
  if (weighted <= TWEET_MAX_CHARS) return null;
  return `${weighted} weighted characters; X allows ${TWEET_MAX_CHARS}`;
}

/**
 * The single word-count currency: readiness gates, max_words caps, and the
 * dashboard's word counts must all agree, so they all call this.
 */
export function wordCount(text: string | null | undefined): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function totalWordCount(texts: readonly (string | null | undefined)[]): number {
  return texts.reduce<number>((sum, t) => sum + wordCount(t), 0);
}

export const TREND_MATERIAL_DEFAULTS = { minScore: 0.7, minWords: 150 };
export const MAX_TREND_CANDIDATE_POSTS = 5;

/** Unpublished trend articles lead the article queue without changing scores. */
export function hasTrendArticlePriority(item: {
  kind: string;
  stage: string;
  meta?: Record<string, unknown> | null;
}): boolean {
  return item.kind === 'article' && item.stage !== 'published' &&
    item.meta?.discoveredBy === 'trends' && typeof item.meta.trendKey === 'string' &&
    item.meta.trendKey.trim().length > 0;
}

export function compareArticleHandoff(
  a: { id: number; kind: string; stage: string; scheduledAt: string | null; meta?: Record<string, unknown> | null },
  b: { id: number; kind: string; stage: string; scheduledAt: string | null; meta?: Record<string, unknown> | null },
): number {
  const priority = Number(hasTrendArticlePriority(b)) - Number(hasTrendArticlePriority(a));
  if (priority) return priority;
  const aAt = a.scheduledAt ? Date.parse(a.scheduledAt) : Infinity;
  const bAt = b.scheduledAt ? Date.parse(b.scheduledAt) : Infinity;
  return aAt - bAt || a.id - b.id;
}

export interface TrendArticleSourcePost {
  id: string;
  url: string;
  text: string;
}

/** Source links/embeds are server metadata, never generated article prose. */
export function trendArticleSourcePost(meta?: Record<string, unknown> | null): TrendArticleSourcePost | null {
  const post = meta?.trendSourcePost as Partial<TrendArticleSourcePost> | undefined;
  if (!post || typeof post.id !== 'string' || !/^\d+$/.test(post.id) || typeof post.text !== 'string') return null;
  return { id: post.id, text: post.text, url: `https://x.com/i/status/${post.id}` };
}

export interface AgentSettingOverride {
  enabled?: boolean;
  schedule?: string | null;
  /** Exact editor tweet hours (0-23); absent means derive them from tweetSlots. */
  tweetSlotHours?: number[];
  /**
   * Free-form bullet list injected verbatim into the agent's prompts.
   * Accumulated from member feedback; deliberately outside FACTORY_SETTINGS so
   * a factory restore never erases it.
   */
  customInstructions?: string;
  [key: string]: unknown;
}

export type SettingsPatch = Partial<Omit<SettingsShape, 'id' | 'updatedAt'>>;

/**
 * Parse one numeric `agentSettings` override. The dashboard and direct API
 * writes may store numbers or numeric strings; anything else (or a
 * non-positive value, unless `allowZero`) falls back so a garbage override
 * never breaks an agent. One definition so every agent coerces identically.
 */
export function numericSetting(
  value: unknown,
  fallback: number,
  opts: { allowZero?: boolean; integer?: boolean } = {},
): number {
  const coerced =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (!Number.isFinite(coerced)) return fallback;
  if (opts.allowZero ? coerced < 0 : coerced <= 0) return fallback;
  return opts.integer ? Math.floor(coerced) : coerced;
}

export interface CostBreakdownItem {
  category: CostCategory;
  label: string;
  costUsd: number;
  last7DaysUsd: number;
  estimatedUsd: number;
  estimatedLast7DaysUsd: number;
}

export interface CostSummary {
  /** Confirmed spend recorded by Tentacle, in USD. */
  totalUsd: number;
  /** Confirmed spend over the trailing seven days, in USD. */
  last7DaysUsd: number;
  /** Older Pi costs calculated locally, excluded from confirmed totals. */
  estimatedUsd: number;
  estimatedLast7DaysUsd: number;
  breakdown: CostBreakdownItem[];
  /** Requests whose provider response did not expose a billable amount yet. */
  unpricedRequests: number;
}

export type XCostOperation =
  | 'post_read'
  | 'owned_post_read'
  | 'user_read'
  | 'trend_read'
  | 'post_create'
  | 'post_create_summoned'
  | 'post_create_with_url'
  | 'follow_create'
  | 'unfollow_delete'
  | 'content_manage'
  | 'media_upload';

export interface XCostBreakdownItem {
  operation: XCostOperation;
  label: string;
  units: number;
  costUsd: number;
}

export interface XCostSummary {
  /** Local estimate from successful interactions recorded after this ledger shipped. */
  totalUsd: number;
  /** Local estimate over the trailing seven days. */
  last7DaysUsd: number;
  breakdown: XCostBreakdownItem[];
  trackedUnits: number;
  /** Successful interactions without a public price mapping, excluded from totalUsd. */
  unpricedInteractions: number;
}

// ---------------------------------------------------------------------------
// Agents / runs
// ---------------------------------------------------------------------------

export interface AgentInfo {
  slug: string;
  name: string;
  description: string;
  kind: 'llm' | 'deterministic';
  defaultSchedule: string | null;
  /** effective schedule after settings overrides */
  schedule: string | null;
  enabled: boolean;
  running: boolean;
  lastRun: RunSummary | null;
  nextRun: string | null;
}

export interface RunSummary {
  id: number;
  agentSlug: string;
  status: RunStatus;
  trigger: RunTrigger;
  startedAt: string;
  finishedAt: string | null;
  summary: string | null;
  error: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  costUsd: string | number | null;
  tracePath: string | null;
  /** true when a JSONL trace exists in Storage for this run — lets the UI decide whether to fetch on expand. */
  hasTrace: boolean;
  meta: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// API response types used by the web app
// ---------------------------------------------------------------------------

export interface HealthResponse {
  ok: true;
  version: string;
}

/** Coarse, content-free aggregates safe to render on the public homepage. */
export interface PublicDashboardResponse {
  generatedAt: string;
  windowDays: 30;
  pipeline: {
    messages: number;
    signal: number;
    ideas: number;
    drafts: number;
    published: number;
  };
  queue: Record<IdeaStage, number>;
  signal: Array<{
    date: string;
    messages: number;
    signal: number;
  }>;
  ideas: Array<{
    kind: IdeaKind;
    stage: IdeaStage;
    score: number | null;
  }>;
  events: Array<{
    agentSlug: string;
    status: RunStatus;
    startedAt: string;
  }>;
}

export interface IdeaSummary {
  id: number;
  kind: IdeaKind;
  title: string | null;
  coreClaim: string | null;
  stage: IdeaStage;
  score: string | number | null;
  scoreComponents: ScoreComponents | null;
  scoreRationale: string | null;
  createdAt: string;
  updatedAt: string;
  scheduledAt: string | null;
  approvalChatId: number | null;
  approvalMessageId: number | null;
  meta?: Record<string, unknown> | null;
}

export interface ArticleSummary {
  id: number;
  ideaId: number | null;
  format: ArticleFormat;
  deliveryStatus: DeliveryStatus;
  title: string | null;
  bodyMd: string | null;
  spans: Span[] | null;
  coverMediaAssetId: number | null;
  xArticleId: string | null;
  xPostId: string | null;
  replyToXPostId: string | null;
  xUrl: string | null;
  publishError: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  meta?: Record<string, unknown> | null;
}

/**
 * The Library is a read model over canonical rows, not another storage table.
 * Media rows and published outputs share this compact list contract; media
 * detail/editing continues through `/api/media/:id`.
 */
export interface LibraryItem {
  key: string;
  objectType: LibraryObjectType;
  sourceId: number;
  kind: LibraryKind;
  origin: LibraryOrigin;
  title: string | null;
  author: string | null;
  body: string | null;
  sourceUrl: string | null;
  canonicalUrl: string | null;
  externalUrl: string | null;
  contentHash: string | null;
  storagePath: string | null;
  tgChatId: number | null;
  tgMessageId: number | null;
  mime: string | null;
  sizeBytes: number | null;
  durationSeconds: number | null;
  coverMediaAssetId: number | null;
  preview: LibraryPreview;
  reservedExcerptCount: number;
  pipeline: {
    fetch: StageStatus;
    transcribe: StageStatus;
    describe: StageStatus;
    extract: StageStatus;
    embed: StageStatus;
    attempts: number;
    lastError: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

export type LibraryPreviewSource =
  | 'original_image'
  | 'article_cover'
  | 'link_image'
  | 'youtube_thumbnail'
  | 'video_still'
  | 'audio_waveform'
  | 'document_preview'
  | 'generated';

export interface LibraryPreview {
  status: 'missing' | 'pending' | 'done' | 'failed';
  source: LibraryPreviewSource | null;
  mediaAssetId: number | null;
  mime: string | null;
  generated: boolean;
}

export interface GoldExample {
  id: number;
  format: ArticleFormat;
  title: string | null;
  body: string;
  enabled: boolean;
  createdAt: string;
}
